import { ref } from 'vue'
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  increment,
  runTransaction
} from 'firebase/firestore'
import { db, auth } from '@/firebase/firebase'

/**
 * 물개 포인트 시스템 Composable
 *
 * 포인트 획득: 출석체크, 상담신청, 회원가입, 이벤트 등
 * 포인트 사용: 상품 교환
 *
 * Firestore 컬렉션:
 * - pointSettings: 포인트 설정 (Admin)
 * - userPoints: 사용자별 포인트 잔액
 * - pointTransactions: 포인트 거래 내역
 * - products: 상품 목록
 * - productOrders: 상품 주문
 */
export function usePoint() {
  const loading = ref(false)
  const error = ref(null)

  // 기본 포인트 설정 값
  const DEFAULT_SETTINGS = {
    signup: 1000,
    dailyCheck: 50,
    streak7: 300,
    streak30: 1500,
    consultation: 500,
    profileComplete: 200,
    appReview: 500,
    referrer: 1000,
    referee: 500,
    visitVerify: 1000,
    contractComplete: 5000,
    snsShare: 100
  }

  /**
   * 현재 사용자 UID 가져오기
   */
  const getCurrentUserId = () => {
    return auth.currentUser?.uid || null
  }

  /**
   * 포인트 설정 가져오기 (Admin 설정)
   */
  const getPointSettings = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'pointSettings', 'default'))
      if (docSnap.exists()) {
        return { ...DEFAULT_SETTINGS, ...docSnap.data() }
      }
      return DEFAULT_SETTINGS
    } catch (err) {
      console.error('포인트 설정 조회 실패:', err)
      return DEFAULT_SETTINGS
    }
  }

  /**
   * 포인트 설정 업데이트 (Admin)
   */
  const updatePointSettings = async (settings) => {
    loading.value = true
    error.value = null

    try {
      await setDoc(doc(db, 'pointSettings', 'default'), {
        ...settings,
        updatedAt: new Date().toISOString()
      }, { merge: true })
      return true
    } catch (err) {
      console.error('포인트 설정 업데이트 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 사용자 포인트 정보 가져오기
   */
  const getUserPoints = async (userId = null) => {
    const uid = userId || getCurrentUserId()
    if (!uid) return null

    try {
      const docSnap = await getDoc(doc(db, 'userPoints', uid))
      if (docSnap.exists()) {
        return { id: uid, ...docSnap.data() }
      }
      return null
    } catch (err) {
      console.error('사용자 포인트 조회 실패:', err)
      return null
    }
  }

  /**
   * 사용자 포인트 초기화 (회원가입 시)
   */
  const initUserPoints = async (userId = null) => {
    const uid = userId || getCurrentUserId()
    if (!uid) return null

    try {
      const existing = await getUserPoints(uid)
      if (existing) return existing

      const now = new Date().toISOString()
      const initialData = {
        balance: 0,
        totalEarned: 0,
        totalUsed: 0,
        lastCheckIn: null,
        currentStreak: 0,
        signupRewarded: false,
        profileRewarded: false,
        appReviewRewarded: false,
        createdAt: now,
        updatedAt: now
      }

      await setDoc(doc(db, 'userPoints', uid), initialData)
      return { id: uid, ...initialData }
    } catch (err) {
      console.error('포인트 초기화 실패:', err)
      return null
    }
  }

  /**
   * 포인트 적립
   */
  const earnPoints = async (amount, reason, relatedId = null) => {
    const uid = getCurrentUserId()
    if (!uid) throw new Error('로그인이 필요합니다')

    loading.value = true
    error.value = null

    try {
      // 사용자 포인트 문서가 없으면 초기화
      let userPoints = await getUserPoints(uid)
      if (!userPoints) {
        userPoints = await initUserPoints(uid)
      }

      const now = new Date().toISOString()
      const newBalance = (userPoints.balance || 0) + amount

      // 포인트 업데이트
      await updateDoc(doc(db, 'userPoints', uid), {
        balance: increment(amount),
        totalEarned: increment(amount),
        updatedAt: now
      })

      // 거래 내역 추가
      await addDoc(collection(db, 'pointTransactions'), {
        userId: uid,
        type: 'earn',
        amount: amount,
        balanceAfter: newBalance,
        reason: reason,
        relatedId: relatedId,
        createdAt: now
      })

      return newBalance
    } catch (err) {
      console.error('포인트 적립 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 포인트 사용
   */
  const usePoints = async (amount, reason, relatedId = null) => {
    const uid = getCurrentUserId()
    if (!uid) throw new Error('로그인이 필요합니다')

    loading.value = true
    error.value = null

    try {
      const userPoints = await getUserPoints(uid)
      if (!userPoints || userPoints.balance < amount) {
        throw new Error('포인트가 부족합니다')
      }

      const now = new Date().toISOString()
      const newBalance = userPoints.balance - amount

      // 포인트 차감
      await updateDoc(doc(db, 'userPoints', uid), {
        balance: increment(-amount),
        totalUsed: increment(amount),
        updatedAt: now
      })

      // 거래 내역 추가
      await addDoc(collection(db, 'pointTransactions'), {
        userId: uid,
        type: 'use',
        amount: -amount,
        balanceAfter: newBalance,
        reason: reason,
        relatedId: relatedId,
        createdAt: now
      })

      return newBalance
    } catch (err) {
      console.error('포인트 사용 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 출석체크
   */
  const checkIn = async () => {
    const uid = getCurrentUserId()
    if (!uid) throw new Error('로그인이 필요합니다')

    loading.value = true
    error.value = null

    try {
      const settings = await getPointSettings()
      let userPoints = await getUserPoints(uid)

      if (!userPoints) {
        userPoints = await initUserPoints(uid)
      }

      // 한국 시간 기준으로 오늘 날짜 계산
      const now = new Date()
      const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
      const today = koreaTime.toISOString().split('T')[0]

      // 마지막 출석일 확인
      const lastCheckIn = userPoints.lastCheckIn
      if (lastCheckIn === today) {
        throw new Error('오늘은 이미 출석체크를 완료했습니다')
      }

      // 연속 출석 계산
      let newStreak = 1
      if (lastCheckIn) {
        const yesterday = new Date(koreaTime)
        yesterday.setDate(yesterday.getDate() - 1)
        const yesterdayStr = yesterday.toISOString().split('T')[0]

        if (lastCheckIn === yesterdayStr) {
          newStreak = (userPoints.currentStreak || 0) + 1
        }
      }

      // 기본 출석 포인트
      let totalPoints = settings.dailyCheck
      let bonusMessage = ''

      // 연속 출석 보너스
      if (newStreak === 7) {
        totalPoints += settings.streak7
        bonusMessage = ` (+${settings.streak7}P 7일 연속 보너스!)`
      } else if (newStreak === 30) {
        totalPoints += settings.streak30
        bonusMessage = ` (+${settings.streak30}P 30일 연속 보너스!)`
      } else if (newStreak % 30 === 0) {
        totalPoints += settings.streak30
        bonusMessage = ` (+${settings.streak30}P ${newStreak}일 연속 보너스!)`
      }

      const nowIso = new Date().toISOString()
      const newBalance = (userPoints.balance || 0) + totalPoints

      // 포인트 및 출석 정보 업데이트
      await updateDoc(doc(db, 'userPoints', uid), {
        balance: increment(totalPoints),
        totalEarned: increment(totalPoints),
        lastCheckIn: today,
        currentStreak: newStreak,
        updatedAt: nowIso
      })

      // 거래 내역 추가
      await addDoc(collection(db, 'pointTransactions'), {
        userId: uid,
        type: 'earn',
        amount: totalPoints,
        balanceAfter: newBalance,
        reason: `출석체크 (${newStreak}일차)${bonusMessage}`,
        relatedId: null,
        createdAt: nowIso
      })

      return {
        points: totalPoints,
        streak: newStreak,
        balance: newBalance,
        bonusMessage
      }
    } catch (err) {
      console.error('출석체크 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 오늘 출석체크 여부 확인
   */
  const hasCheckedInToday = async () => {
    const uid = getCurrentUserId()
    if (!uid) return false

    try {
      const userPoints = await getUserPoints(uid)
      if (!userPoints || !userPoints.lastCheckIn) return false

      const now = new Date()
      const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
      const today = koreaTime.toISOString().split('T')[0]

      return userPoints.lastCheckIn === today
    } catch {
      return false
    }
  }

  /**
   * 회원가입 보상 지급
   */
  const rewardSignup = async () => {
    const uid = getCurrentUserId()
    if (!uid) return false

    try {
      let userPoints = await getUserPoints(uid)
      if (!userPoints) {
        userPoints = await initUserPoints(uid)
      }

      if (userPoints.signupRewarded) {
        return false // 이미 지급됨
      }

      const settings = await getPointSettings()
      await earnPoints(settings.signup, '회원가입 축하 포인트')

      await updateDoc(doc(db, 'userPoints', uid), {
        signupRewarded: true
      })

      return true
    } catch (err) {
      console.error('회원가입 보상 실패:', err)
      return false
    }
  }

  /**
   * 상담 신청 보상 지급
   */
  const rewardConsultation = async (consultationId) => {
    const uid = getCurrentUserId()
    if (!uid) return false

    try {
      const settings = await getPointSettings()
      await earnPoints(settings.consultation, '상담 신청 포인트', consultationId)
      return true
    } catch (err) {
      console.error('상담 신청 보상 실패:', err)
      return false
    }
  }

  /**
   * 포인트 거래 내역 조회
   */
  const getTransactions = async (limitCount = 50) => {
    const uid = getCurrentUserId()
    if (!uid) return []

    try {
      const q = query(
        collection(db, 'pointTransactions'),
        where('userId', '==', uid),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('거래 내역 조회 실패:', err)
      return []
    }
  }

  /**
   * 현재 잔액 조회
   */
  const getBalance = async () => {
    const userPoints = await getUserPoints()
    return userPoints?.balance || 0
  }

  // ==================== 상품 관련 ====================

  /**
   * 상품 목록 조회
   */
  const getProducts = async (category = null) => {
    try {
      let q
      if (category) {
        q = query(
          collection(db, 'products'),
          where('isActive', '==', true),
          where('category', '==', category),
          orderBy('order', 'asc')
        )
      } else {
        q = query(
          collection(db, 'products'),
          where('isActive', '==', true),
          orderBy('order', 'asc')
        )
      }

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('상품 조회 실패:', err)
      return []
    }
  }

  /**
   * 전체 상품 목록 조회 (Admin)
   */
  const getAllProducts = async () => {
    try {
      const q = query(
        collection(db, 'products'),
        orderBy('order', 'asc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('전체 상품 조회 실패:', err)
      return []
    }
  }

  /**
   * 상품 상세 조회
   */
  const getProduct = async (productId) => {
    try {
      const docSnap = await getDoc(doc(db, 'products', productId))
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() }
      }
      return null
    } catch (err) {
      console.error('상품 상세 조회 실패:', err)
      return null
    }
  }

  /**
   * 상품 등록 (Admin)
   */
  const createProduct = async (productData) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        isActive: true,
        createdAt: now,
        updatedAt: now
      })
      return docRef.id
    } catch (err) {
      console.error('상품 등록 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 상품 수정 (Admin)
   */
  const updateProduct = async (productId, productData) => {
    loading.value = true
    error.value = null

    try {
      await updateDoc(doc(db, 'products', productId), {
        ...productData,
        updatedAt: new Date().toISOString()
      })
      return true
    } catch (err) {
      console.error('상품 수정 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 상품 교환 (주문 생성)
   */
  const purchaseProduct = async (productId) => {
    const uid = getCurrentUserId()
    if (!uid) throw new Error('로그인이 필요합니다')

    loading.value = true
    error.value = null

    try {
      // 상품 정보 조회
      const product = await getProduct(productId)
      if (!product) {
        throw new Error('상품을 찾을 수 없습니다')
      }
      if (!product.isActive) {
        throw new Error('판매 중지된 상품입니다')
      }
      if (product.stock === 0) {
        throw new Error('품절된 상품입니다')
      }

      // 잔액 확인
      const userPoints = await getUserPoints(uid)
      if (!userPoints || userPoints.balance < product.price) {
        throw new Error('포인트가 부족합니다')
      }

      // 사용자 정보 조회
      const userDoc = await getDoc(doc(db, 'users', uid))
      const userData = userDoc.exists() ? userDoc.data() : {}

      const now = new Date().toISOString()

      // 트랜잭션으로 처리
      await runTransaction(db, async (transaction) => {
        // 포인트 차감
        const userPointsRef = doc(db, 'userPoints', uid)
        transaction.update(userPointsRef, {
          balance: increment(-product.price),
          totalUsed: increment(product.price),
          updatedAt: now
        })

        // 재고 차감 (무제한이 아닌 경우)
        if (product.stock > 0) {
          const productRef = doc(db, 'products', productId)
          transaction.update(productRef, {
            stock: increment(-1),
            updatedAt: now
          })
        }
      })

      // 주문 생성
      const orderRef = await addDoc(collection(db, 'productOrders'), {
        userId: uid,
        userEmail: userData.email || '',
        userName: userData.name || '',
        userPhone: userData.phone || '',

        productId: productId,
        productName: product.name,
        productBrand: product.brand || '',
        price: product.price,

        status: 'pending',
        giftMessage: null,
        giftImageUrl: null,
        completedAt: null,
        completedBy: null,

        createdAt: now,
        updatedAt: now
      })

      // 포인트 거래 내역 추가
      const newBalance = userPoints.balance - product.price
      await addDoc(collection(db, 'pointTransactions'), {
        userId: uid,
        type: 'use',
        amount: -product.price,
        balanceAfter: newBalance,
        reason: `상품 교환: ${product.name}`,
        relatedId: orderRef.id,
        createdAt: now
      })

      return orderRef.id
    } catch (err) {
      console.error('상품 교환 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 내 주문 내역 조회
   */
  const getMyOrders = async () => {
    const uid = getCurrentUserId()
    if (!uid) return []

    try {
      const q = query(
        collection(db, 'productOrders'),
        where('userId', '==', uid),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('주문 내역 조회 실패:', err)
      return []
    }
  }

  /**
   * 전체 주문 목록 조회 (Admin)
   */
  const getAllOrders = async (status = null) => {
    try {
      let q
      if (status) {
        q = query(
          collection(db, 'productOrders'),
          where('status', '==', status),
          orderBy('createdAt', 'desc')
        )
      } else {
        q = query(
          collection(db, 'productOrders'),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('전체 주문 조회 실패:', err)
      return []
    }
  }

  /**
   * 주문 완료 처리 - 기프티콘 등록 (Admin)
   */
  const completeOrder = async (orderId, giftMessage, giftImageUrl = null) => {
    loading.value = true
    error.value = null

    try {
      const adminUid = getCurrentUserId()
      const now = new Date().toISOString()

      await updateDoc(doc(db, 'productOrders', orderId), {
        status: 'completed',
        giftMessage: giftMessage,
        giftImageUrl: giftImageUrl,
        completedAt: now,
        completedBy: adminUid,
        updatedAt: now
      })

      return true
    } catch (err) {
      console.error('주문 완료 처리 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 주문 취소 및 환불 (Admin)
   */
  const cancelOrder = async (orderId) => {
    loading.value = true
    error.value = null

    try {
      // 주문 정보 조회
      const orderDoc = await getDoc(doc(db, 'productOrders', orderId))
      if (!orderDoc.exists()) {
        throw new Error('주문을 찾을 수 없습니다')
      }

      const order = orderDoc.data()
      if (order.status !== 'pending') {
        throw new Error('대기 상태의 주문만 취소할 수 있습니다')
      }

      const now = new Date().toISOString()

      // 트랜잭션으로 처리
      await runTransaction(db, async (transaction) => {
        // 포인트 환불
        const userPointsRef = doc(db, 'userPoints', order.userId)
        transaction.update(userPointsRef, {
          balance: increment(order.price),
          totalUsed: increment(-order.price),
          updatedAt: now
        })

        // 주문 상태 변경
        const orderRef = doc(db, 'productOrders', orderId)
        transaction.update(orderRef, {
          status: 'cancelled',
          updatedAt: now
        })

        // 재고 복구
        const productRef = doc(db, 'products', order.productId)
        const productDoc = await transaction.get(productRef)
        if (productDoc.exists() && productDoc.data().stock >= 0) {
          transaction.update(productRef, {
            stock: increment(1),
            updatedAt: now
          })
        }
      })

      // 포인트 거래 내역 추가 (환불)
      const userPoints = await getUserPoints(order.userId)
      await addDoc(collection(db, 'pointTransactions'), {
        userId: order.userId,
        type: 'earn',
        amount: order.price,
        balanceAfter: (userPoints?.balance || 0) + order.price,
        reason: `주문 취소 환불: ${order.productName}`,
        relatedId: orderId,
        createdAt: now
      })

      return true
    } catch (err) {
      console.error('주문 취소 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    DEFAULT_SETTINGS,

    // 설정
    getPointSettings,
    updatePointSettings,

    // 사용자 포인트
    getUserPoints,
    initUserPoints,
    getBalance,

    // 포인트 적립/사용
    earnPoints,
    usePoints,
    getTransactions,

    // 출석체크
    checkIn,
    hasCheckedInToday,

    // 보상
    rewardSignup,
    rewardConsultation,

    // 상품
    getProducts,
    getAllProducts,
    getProduct,
    createProduct,
    updateProduct,
    purchaseProduct,

    // 주문
    getMyOrders,
    getAllOrders,
    completeOrder,
    cancelOrder
  }
}
